<script setup lang="ts">
import type { Meta } from '@brighterly/lib-core-types/api/interfaces'
import type { CustomerReferral } from '@entities/customer'

import { AppText } from '@shared/ui/typography'
import { AppTable, AppTd, AppTh, AppTr } from '@shared/ui/tables'
import { LinkTextPrimary } from '@shared/ui/links'
import { AppBadge } from '@shared/ui/badges'

defineOptions({
  name: 'CustomerReferralsTable',
})

defineProps<{
  referrals: CustomerReferral[]
  meta: Meta
  loading: boolean
  emptyCell: string
  formatDate: (value: string | null) => string
  getCustomerPath: (customerId: number) => string
}>()

const emit = defineEmits<{
  'change:page': [page: number]
  'change:per-page': [perPage: number]
}>()

const COLUMNS = ['ID', 'Name', 'Email', 'Registered', 'First purchase', 'Credited']
</script>

<template>
  <AppTable
    v-if="referrals.length || loading"
    class="rounded-table"
    :meta="meta"
    :loading="loading"
    @change:page="page => emit('change:page', page)"
    @change:per-page="perPage => emit('change:per-page', perPage)"
  >
    <template #head>
      <AppTr>
        <AppTh
          v-for="column in COLUMNS"
          :key="column"
        >
          <AppText
            name="body1-medium"
            color="slate-900"
          >
            {{ column }}
          </AppText>
        </AppTh>
      </AppTr>
    </template>
    <template #body>
      <AppTr
        v-for="referral in referrals"
        :key="referral.id"
      >
        <AppTd class="medium">
          <LinkTextPrimary :href="getCustomerPath(referral.id)">
            #{{ referral.id }}
          </LinkTextPrimary>
        </AppTd>
        <AppTd class="medium">
          <AppText name="body1">
            {{ referral.name || emptyCell }}
          </AppText>
        </AppTd>
        <AppTd class="medium">
          <AppText name="body1">
            {{ referral.email || emptyCell }}
          </AppText>
        </AppTd>
        <AppTd class="medium">
          <AppText name="body1">
            {{ formatDate(referral.registered_at) }}
          </AppText>
        </AppTd>
        <AppTd class="medium">
          <AppText name="body1">
            {{ formatDate(referral.purchased_at) }}
          </AppText>
        </AppTd>
        <AppTd class="medium">
          <AppBadge
            :color="referral.credited ? 'blue' : 'slate'"
            :text="referral.credited ? 'Credited' : 'Pending'"
          />
        </AppTd>
      </AppTr>
    </template>
  </AppTable>
  <div
    v-else
    class="customer-referrals-table__empty"
  >
    <AppText
      name="body1"
      color="slate-500"
    >
      No referrals yet.
    </AppText>
  </div>
</template>

<style scoped lang="scss" src="./CustomerReferralsTable.scss" />
